// What both cloud's server and its screen need about conversations between
// people: names, limits and the shapes the REST answers have. No dependencies,
// so the screen imports it as `@trameria/cloud-api/conversations-shared`
// without pulling server code in.
//
// Promise:
// - A topic lives in one place: a space of a workspace, or a direct message
//   between 2 to DM_MEMBERS_MAX people (inside a workspace, or outside every
//   workspace). The same people have one direct message per place.
// - Answers never say whether something hidden exists: a topic in a space one
//   cannot see, or a direct message between others, is answered like one that
//   is not there (404).
// - Message numbers (seq) run 1, 2, 3, ... in each topic without gaps; a
//   removed message keeps its number with an empty body.

export const TOPIC_NAME_MAX = 100;
export const MESSAGE_BODY_MAX = 20_000;
export const MESSAGE_FILES_MAX = 10;
export const MESSAGE_PAGE_MAX = 200;
export const TOPIC_PAGE_MAX = 100;
/** The label of a topic without a name, cut from its first message. */
export const TOPIC_LABEL_MAX = 60;

/** The most people of a direct message, oneself included (as Discord's group DMs). */
export const DM_MEMBERS_MAX = 10;

/** How a user id looks (anything else in a direct message's path is nobody). */
export const USER_ID_PATTERN = /^[A-Za-z0-9_-]+$/;

/** People's ids in byte order (JavaScript's `<`; the ids are ASCII), each once. */
export const dmPeersOrder = (ids: readonly string[]): string[] =>
  [...new Set(ids)].sort();

/**
 * The path of a direct message (`…/dms/<this>`): the other people's ids in
 * byte order, joined by ",". One person's is their id.
 */
export const dmPeersPath = (ids: readonly string[]): string =>
  dmPeersOrder(ids).join(",");

/**
 * The ids in a direct message's path, as written (any order, repeats kept);
 * null when one of them is not an id.
 */
export function parseDmPeers(path: string): string[] | null {
  const ids = path.split(",");
  return ids.every((id) => USER_ID_PATTERN.test(id)) ? ids : null;
}

export const EMOJI_NAME_MAX = 32;
export const EMOJI_NAME_PATTERN = new RegExp(
  `^[a-z0-9_-]{1,${EMOJI_NAME_MAX}}$`,
);
export const EMOJI_MAX_PER_WORKSPACE = 500;
export const EMOJI_FILE_MAX_BYTES = 256 * 1024;

/** Reactions that are words, not emoji. */
export const RESERVED_REACTIONS = ["check", "eye"] as const;
export const REACTION_KIND_MAX = 40;

/** A reaction kind: a reserved word, one emoji, or `:name:` of a custom emoji. */
export function reactionKindProblem(kind: string): "format" | null {
  if ((RESERVED_REACTIONS as readonly string[]).includes(kind)) return null;
  const custom = /^:([a-z0-9_-]{1,32}):$/.exec(kind);
  if (custom) return null;
  if (kind.length > 16) return "format";
  const segments = [...new Intl.Segmenter().segment(kind)];
  if (segments.length !== 1) return "format";
  return /\p{Extended_Pictographic}/u.test(kind) ? null : "format";
}

/** The custom emoji name of a reaction kind (`:name:`), or null. */
export const customEmojiName = (kind: string): string | null =>
  /^:([a-z0-9_-]{1,32}):$/.exec(kind)?.[1] ?? null;

/** How names compare in a space: trimmed, spaces folded, lower case. */
export const normalizeTopicName = (name: string): string =>
  name.trim().replace(/\s+/g, " ").toLowerCase();

export const TOPIC_PLACES = ["space", "dm"] as const;
export type TopicPlace = (typeof TOPIC_PLACES)[number];

/** topic = a conversation; broadcast = an announcement (one message, no replies). */
export const TOPIC_KINDS = ["topic", "broadcast"] as const;
export type TopicKind = (typeof TOPIC_KINDS)[number];

// ---- answers ------------------------------------------------------------------

export interface PersonRef {
  userId: string;
  name: string;
  avatarFileId: string | null;
}

/** A topic as lists and the topic screen show it. */
export interface TopicView {
  id: string;
  key: string;
  /** null for a personal direct message. */
  workspaceId: string | null;
  place: TopicPlace;
  spaceId: string | null;
  kind: TopicKind;
  /** The name, or null for a topic without one. */
  name: string | null;
  /** What to show: the name, or the start of the first message. */
  label: string;
  lastSeq: number;
  lastMessageAt: string | null;
  createdAt: string;
  createdBy: PersonRef | null;
  /**
   * For a direct message: the others it was made for, by name (empty
   * otherwise); the one with myself alone has me. Their ids make its path
   * (`dmPeersPath`); one whose account is gone keeps a place here with an
   * empty name.
   */
  peers: PersonRef[];
  /** My read point and how many messages after it are not mine. */
  readSeq: number;
  unread: number;
  /** The person may post (feature on, permission, still a member for a direct message, not a broadcast). */
  canPost: boolean;
}

/** A row of the direct messages' list: the topic and its last message. */
export interface DmListView extends TopicView {
  /**
   * The last message not removed: its start on one line (whitespace folded)
   * and who wrote it. null when there is none.
   */
  lastMessage: { text: string; author: PersonRef | null } | null;
}

export interface MessageFileView {
  id: string;
  name: string;
  mime: string;
  size: number;
  /** An image's size when it was measured at upload (the thumbnail's box). */
  width: number | null;
  height: number | null;
}

export interface ReactionView {
  kind: string;
  count: number;
  mine: boolean;
}

export interface MessageView {
  id: string;
  topicId: string;
  seq: number;
  /** "" when removed. */
  body: string;
  author: PersonRef | null;
  /** Written by the person's agent or app through this credential (its name). */
  via: { credentialId: string; name: string } | null;
  files: MessageFileView[];
  reactions: ReactionView[];
  createdAt: string;
  editedAt: string | null;
  removedAt: string | null;
  /** The viewer may edit and remove it (the writer, or the writer's own agent). */
  mine: boolean;
}

/** GET …/topics/:id/messages → newest last. */
export interface MessagePage {
  messages: MessageView[];
  /** Whether there are older ones before the first. */
  more: boolean;
}

/** GET …/topics → { topics, cursor } (newest activity first). */
export interface TopicPage {
  topics: TopicView[];
  cursor: string | null;
}

/** POST …/messages, POST …/topics, POST …/dms/:userIds/messages. */
export interface PostResult {
  topic: TopicView;
  message: MessageView;
}

/**
 * GET …/dms/:userIds and GET /api/dms/:userIds: the direct message with
 * these people (`dmPeersPath`). `topic` is null until the first message.
 */
export interface DmView {
  /** The others, by name. */
  peers: PersonRef[];
  topic: TopicView | null;
  /**
   * The viewer may write: the feature is on and someone else is still a
   * member (for a personal one, still has their account).
   */
  canPost: boolean;
}

export interface EmojiView {
  id: string;
  name: string;
  fileId: string;
  createdBy: PersonRef | null;
  createdAt: string;
}

/** `page`: a workspace's document (when the Workspace documents part is there). */
export const PIN_KINDS = ["topic", "page"] as const;
export type PinKind = (typeof PIN_KINDS)[number];

/** A pinned document as the viewer sees it. */
export interface PinnedDocument {
  id: string;
  key: string;
  title: string;
  kind: string;
}

export interface PinView {
  kind: PinKind;
  targetId: string;
  /** What to show; null when the pinned thing is gone or hidden from the viewer. */
  topic: TopicView | null;
  /** The same for a pinned document (kind `page`). */
  page: PinnedDocument | null;
  position: number;
}

/** GET …/spaces/:id/overview */
export interface SpaceOverview {
  spaceId: string;
  description: string;
  members: PersonRef[];
  memberCount: number;
  pins: PinView[];
  recentTopics: TopicView[];
  /** The viewer may change the pins. */
  canPin: boolean;
}

/** Why a topic is in the Inbox. */
export const INBOX_REASONS = ["dm", "mention", "posted"] as const;
export type InboxReason = (typeof INBOX_REASONS)[number];

/** GET …/inbox → { items } (topics with unread messages that concern me). */
export interface InboxItem {
  topic: TopicView;
  reason: InboxReason;
}

/** GET …/unreads → per topic, only those with unread messages. */
export interface UnreadSummary {
  topics: { topicId: string; unread: number; readSeq: number }[];
}

/** This part's team event types and their payloads (ids only; the screen reads again). */
export interface ConversationTeamEvents {
  "topic.created": { topicId: string };
  /** Renamed or moved. */
  "topic.changed": { topicId: string };
  "message.created": { topicId: string; messageId: string; seq: number };
  /** Edited, removed, or its reactions changed. */
  "message.changed": { topicId: string; messageId: string };
  /** Only to the person who read. */
  "read.changed": { topicId: string };
  "emoji.changed": Record<string, never>;
  "pin.changed": { spaceId: string };
}

/** Core event types (Me's stream) for personal direct messages, one per person. */
export interface PersonalDmEvents {
  "dm.message": { topicId: string; messageId: string; seq: number };
  "dm.changed": { topicId: string; messageId?: string };
}
